import { useMemo, useState } from 'react';
import { Alert, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import Badge from '../base/Badge';
import Button from '../base/Button';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableServerFooter from '../base/AdvanceTableServerFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';

import { fetchCompletedJobs } from '../../features/jobs/api/jobsApi';
import { cableLabel } from '../../helpers/cableLabel';
import { formatLength } from '../../helpers/formatLength';
import { colorNameFromKey, productColorBadgeStyle } from '../../helpers/productColor';

/**
 * Everything the factory has finished, newest first.
 *
 * A finished lot used to be reachable only by opening the machine it ran on,
 * digging into its execution history and clicking through from there — three
 * screens deep for the single most common question anybody asks about a
 * finished product, which is "show me it".
 *
 * Paged on the SERVER, and deliberately so. This list only grows: a year of
 * production is tens of thousands of rows, and asking for all of them to draw
 * twenty-five is a download, not a page size. Each page is one indexed query,
 * and the backend caps the size whatever the caller asks for.
 *
 * `keepPreviousData` is what makes paging feel like paging — without it the
 * table empties and the spinner flashes on every click, which reads as the data
 * having gone away. The footer shows a small spinner instead, next to totals
 * that come from the server rather than from the twenty-five rows on screen.
 */

const PAGE_SIZE = 25;

const when = ts => {
  if (!ts) return '—';
  const d = new Date(Number(ts));
  return Number.isNaN(d.getTime())
    ? '—'
    : new Intl.DateTimeFormat('ro-RO', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(d);
};

const CompletedProjectsPanel = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['jobs', 'completed', page, q],
    queryFn: () => fetchCompletedJobs({ page, limit: PAGE_SIZE, q }),
    placeholderData: keepPreviousData,
    staleTime: 10_000
  });

  const rows = useMemo(() => data?.jobs ?? [], [data]);
  const pages = data?.pages ?? 1;
  const total = data?.total ?? 0;

  const columns = useMemo(
    () => [
      {
        accessorKey: 'productLabel',
        header: 'Product',
        cell: ({ row: { original: j } }) => (
          <span className="fw-semibold">{cableLabel(j, j.productLabel)}</span>
        ),
        meta: { cellProps: { className: 'text-900' } }
      },
      {
        id: 'colour',
        header: 'Colour',
        // Sorting is the server's job here, so the header is plain text. A
        // sortable header on a server-paged table sorts only what is on screen,
        // which silently reorders 25 rows out of nine hundred pages.
        enableSorting: false,
        cell: ({ row: { original: j } }) => {
          const colour = colorNameFromKey(j.productKey);
          return colour ? (
            <span className="badge fs-10 text-uppercase" style={productColorBadgeStyle(colour)}>
              {colour}
            </span>
          ) : (
            <span className="text-700">—</span>
          );
        }
      },
      {
        accessorKey: 'jobCode',
        header: 'Job',
        enableSorting: false,
        cell: ({ row: { original: j } }) => (
          <span className="font-monospace text-700">
            {j.jobCode || j.jobId?.slice(0, 8)}
          </span>
        )
      },
      {
        accessorKey: 'plannedLength',
        header: 'Length',
        enableSorting: false,
        cell: ({ row: { original: j } }) => formatLength(j.plannedLength),
        meta: { headerProps: { className: 'text-end' }, cellProps: { className: 'text-end' } }
      },
      {
        accessorKey: 'completedAt',
        header: 'Finished',
        enableSorting: false,
        cell: ({ row: { original: j } }) => when(j.completedAt),
        meta: { cellProps: { className: 'white-space-nowrap' } }
      },
      {
        id: 'where',
        header: 'Where',
        enableSorting: false,
        cell: ({ row: { original: j } }) => (
          <Badge bg={j.archived ? 'info' : 'success'} variant="phoenix" className="fs-10">
            {j.archived ? 'Archived' : 'In production storage'}
          </Badge>
        )
      },
      {
        id: 'open',
        header: '',
        enableSorting: false,
        cell: ({ row: { original: j } }) => (
          <Link
            to={`/apps/project-management/project-details/${j.jobId}`}
            className="btn btn-phoenix-secondary btn-sm py-0 px-2 fs-10"
            title="Open this project"
            onClick={e => e.stopPropagation()}
          >
            Open
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-1 fs-10" />
          </Link>
        ),
        meta: { headerProps: { style: { width: 90 } }, cellProps: { className: 'text-end' } }
      }
    ],
    []
  );

  const table = useAdvanceTable({
    data: rows,
    columns,
    pageSize: PAGE_SIZE,
    pagination: true,
    sortable: false,
    // The server has already sliced this page; slicing it again here would show
    // one row of twenty-five.
    manualPagination: true,
    pageCount: pages
  });

  const submit = e => {
    e.preventDefault();
    // Back to page one: page four of a search nobody has run yet is empty.
    setPage(1);
    setQ(search.trim());
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-1100 mb-0">
              Completed <span className="fw-normal text-700 fs-7">({total.toLocaleString()})</span>
            </h3>
            <p className="text-700 fs-9 mb-0">
              Most recently finished first. Click a row to open it.
              {data?.counts?.archived > 0 && (
                <span className="text-700">
                  {' '}
                  {data.counts.archived.toLocaleString()} archived,{' '}
                  {data.counts.live} still in production storage.
                </span>
              )}
            </p>
          </div>

          <Form onSubmit={submit}>
            <InputGroup style={{ maxWidth: 320 }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faMagnifyingGlass} className="fs-10" />
              </InputGroup.Text>
              <Form.Control
                size="sm"
                placeholder="product or job code…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <Button type="submit" size="sm" variant="phoenix-secondary">
                Search
              </Button>
            </InputGroup>
          </Form>
        </div>

        {isError && (
          <Alert variant="danger" className="py-2 fs-9">
            {error?.response?.data?.error || error?.message || 'Could not load completed projects'}
          </Alert>
        )}

        {isLoading ? (
          <div className="d-flex justify-content-center py-6">
            <Spinner animation="border" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-700">
            {q ? `Nothing finished matches “${q}”.` : 'Nothing finished yet.'}
          </div>
        ) : (
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{ size: 'sm', className: 'phoenix-table fs-9 mb-0' }}
              rowClassName="align-middle"
              getRowProps={row => ({
                style: { cursor: 'pointer' },
                onClick: () =>
                  navigate(`/apps/project-management/project-details/${row.original.jobId}`)
              })}
            />
            <AdvanceTableServerFooter
              className="mt-3"
              page={page}
              pages={pages}
              total={total}
              pageSize={PAGE_SIZE}
              onPage={setPage}
              isFetching={isFetching}
            />
          </AdvanceTableProvider>
        )}
      </Card.Body>
    </Card>
  );
};

export default CompletedProjectsPanel;
